/**
 * Classificação de campos do prontuário/perfil do paciente
 * e helpers de payload para Meu Perfil (fonte única de edição).
 */
import { formatPhone, formatCpf, formatCep, stripMask } from '@/utils/masks';

export type PatientProfileFieldKind = 'editable' | 'readonly' | 'system';

/** Inventário alinhado ao prontuário (EHRHeader + Informações Rápidas). */
export const PATIENT_PROFILE_FIELD_INVENTORY = {
  // Cadastro pessoal (paciente)
  name: 'readonly',
  email: 'readonly',
  phone: 'editable',
  cpf: 'editable',
  dateOfBirth: 'editable',
  gender: 'editable',
  isBrazilian: 'editable',
  country: 'editable',
  // Endereço
  street: 'editable',
  number: 'editable',
  complement: 'editable',
  neighborhood: 'editable',
  city: 'editable',
  state: 'editable',
  zipCode: 'editable',
  // Clínico inerente ao paciente
  bloodType: 'editable',
  height: 'editable',
  weight: 'editable',
  allergies: 'editable',
  medications: 'editable',
  medicalHistory: 'editable',
  emergencyContact: 'editable',
  // Derivado / sistema / clínico do médico
  imc: 'system',
  lastConsultation: 'system',
  diagnosis: 'system',
  consultationHistory: 'system',
  cumulativeRecord: 'system',
  status: 'system',
} as const satisfies Record<string, PatientProfileFieldKind>;

export type EditablePatientProfileField = {
  [K in keyof typeof PATIENT_PROFILE_FIELD_INVENTORY]: (typeof PATIENT_PROFILE_FIELD_INVENTORY)[K] extends 'editable'
    ? K
    : never;
}[keyof typeof PATIENT_PROFILE_FIELD_INVENTORY];

export const EDITABLE_PATIENT_PROFILE_FIELDS = (
  Object.entries(PATIENT_PROFILE_FIELD_INVENTORY) as Array<
    [keyof typeof PATIENT_PROFILE_FIELD_INVENTORY, PatientProfileFieldKind]
  >
)
  .filter(([, kind]) => kind === 'editable')
  .map(([key]) => key) as EditablePatientProfileField[];

/** Rota canônica de edição de dados do paciente. */
export const PATIENT_PROFILE_PATH = '/perfil';

/** Settings não edita mais dados clínicos/cadastrais do paciente. */
export const PATIENT_SETTINGS_REDIRECTS_TO_PROFILE = true;

export type PatientProfileFormValues = {
  phone: string;
  cpf: string;
  dateOfBirth: string;
  gender: string;
  isBrazilian: string;
  country: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  bloodType: string;
  height: string;
  weight: string;
  allergies: string;
  medications: string;
  medicalHistory: string;
  emergencyContact: string;
};

export function emptyPatientProfileFormValues(): PatientProfileFormValues {
  return {
    phone: '',
    cpf: '',
    dateOfBirth: '',
    gender: '',
    isBrazilian: '',
    country: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    bloodType: '',
    height: '',
    weight: '',
    allergies: '',
    medications: '',
    medicalHistory: '',
    emergencyContact: '',
  };
}

export function patientDtoToFormValues(patient: {
  phone?: string | null;
  cpf?: string | null;
  dateOfBirth?: string | Date | null;
  gender?: string | null;
  isBrazilian?: boolean | null;
  country?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  bloodType?: string | null;
  height?: number | null;
  weight?: number | null;
  allergies?: string | null;
  medications?: string | null;
  medicalHistory?: string | null;
  emergencyContact?: string | null;
}): PatientProfileFormValues {
  const dob = patient.dateOfBirth
    ? String(patient.dateOfBirth).slice(0, 10)
    : '';

  return {
    phone: formatPhone(patient.phone) || '',
    cpf: formatCpf(patient.cpf) || '',
    dateOfBirth: dob,
    gender: patient.gender || '',
    isBrazilian: patient.isBrazilian !== undefined && patient.isBrazilian !== null ? String(patient.isBrazilian) : '',
    country: patient.country || '',
    street: patient.street || '',
    number: patient.number || '',
    complement: patient.complement || '',
    neighborhood: patient.neighborhood || '',
    city: patient.city || '',
    state: patient.state || '',
    zipCode: formatCep(patient.zipCode) || '',
    bloodType: patient.bloodType || '',
    height: patient.height != null ? String(patient.height) : '',
    weight: patient.weight != null ? String(patient.weight) : '',
    allergies: patient.allergies || '',
    medications: patient.medications || '',
    medicalHistory: patient.medicalHistory || '',
    emergencyContact: patient.emergencyContact || '',
  };
}

export type PatientProfileUpdatePayload = {
  phone?: string;
  cpf?: string;
  dateOfBirth?: string;
  gender?: string;
  isBrazilian?: boolean;
  country?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  bloodType?: string;
  height?: number;
  weight?: number;
  allergies?: string;
  medications?: string;
  medicalHistory?: string;
  emergencyContact?: string;
};

export type PatientProfileValidationResult = {
  valid: boolean;
  errors: Partial<Record<keyof PatientProfileFormValues, string>>;
  payload?: PatientProfileUpdatePayload;
};

export function validateAndBuildPatientProfilePayload(
  values: PatientProfileFormValues,
): PatientProfileValidationResult {
  const errors: Partial<Record<keyof PatientProfileFormValues, string>> = {};

  const heightRaw = values.height.trim();
  const weightRaw = values.weight.trim();
  let height: number | undefined;
  let weight: number | undefined;

  if (heightRaw) {
    height = parseFloat(heightRaw);
    if (isNaN(height) || height <= 0 || height > 3) {
      errors.height = 'Informe uma altura válida (ex: 1.75)';
    }
  }

  if (weightRaw) {
    weight = parseFloat(weightRaw);
    if (isNaN(weight) || weight <= 0 || weight > 500) {
      errors.weight = 'Informe um peso válido (ex: 70)';
    }
  }

  if (values.state.trim() && values.state.trim().length !== 2) {
    errors.state = 'UF deve ter 2 letras';
  }

  if (Object.keys(errors).length > 0) {
    return { valid: false, errors };
  }

  let dateOfBirth: string | undefined;
  if (values.dateOfBirth.trim()) {
    const iso =
      values.dateOfBirth.trim().length === 10
        ? new Date(values.dateOfBirth.trim()).toISOString()
        : values.dateOfBirth.trim();
    dateOfBirth = iso;
  }

  const payload: PatientProfileUpdatePayload = {
    phone: stripMask(values.phone) || undefined,
    cpf: stripMask(values.cpf) || undefined,
    dateOfBirth,
    gender: values.gender || undefined,
    isBrazilian: values.isBrazilian === 'true' ? true : values.isBrazilian === 'false' ? false : undefined,
    country: values.country.trim() || undefined,
    street: values.street.trim() || undefined,
    number: values.number.trim() || undefined,
    complement: values.complement.trim() || undefined,
    neighborhood: values.neighborhood.trim() || undefined,
    city: values.city.trim() || undefined,
    state: values.state.trim() ? values.state.trim().toUpperCase() : undefined,
    zipCode: stripMask(values.zipCode) || undefined,
    bloodType: values.bloodType || undefined,
    height,
    weight,
    allergies: values.allergies.trim(),
    medications: values.medications.trim(),
    medicalHistory: values.medicalHistory.trim(),
    emergencyContact: values.emergencyContact.trim() || undefined,
  };

  return { valid: true, errors: {}, payload };
}
